import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Check, Heart, Calendar, Clock } from "lucide-react";
import { toast } from "sonner";

const LOGO = "https://customer-assets.emergentagent.com/job_writer-shop-3/artifacts/2m0m319p_before%20the%20vows%20logo.png";

const PROGRAMS = [
  {
    key: "7-week",
    weeks: 7,
    price: 525,
    label: "Foundations — 7 weeks",
    blurb: "The essentials. Seven sessions covering identity in Christ, biblical roles in marriage, communication, finances, intimacy, conflict, and lifelong vision.",
    bullets: ["7 weekly 1-hour sessions", "Custom Oneness Apostolic curriculum", "Workbook + scripture readings", "Pre-wedding wrap-up meeting"],
  },
  {
    key: "12-week",
    weeks: 12,
    price: 900,
    label: "Comprehensive — 12 weeks",
    blurb: "The full depth. Twelve sessions including everything in Foundations plus extended teaching on family-of-origin, expectations, spiritual leadership, and Apostolic distinctives in the home.",
    bullets: ["12 weekly 1-hour sessions", "Custom Oneness Apostolic curriculum", "Workbook + scripture readings", "Personality + expectations assessments", "Pre-wedding + 30-day post-wedding check-in"],
    best: true,
  },
];

export default function BeforeTheVows() {
  const [form, setForm] = useState({
    groom_name: "", bride_name: "", email: "", phone: "",
    wedding_date: "", program: "7-week", message: "",
  });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/inquiries/marriage", form);
      setSent(true);
      toast.success("Inquiry sent. Cody will be in touch soon.");
      setForm({ groom_name: "", bride_name: "", email: "", phone: "", wedding_date: "", program: "7-week", message: "" });
    } catch {
      toast.error("Could not send. Please try again or email thedisciplelifems@gmail.com");
    } finally {
      setBusy(false);
    }
  };

  const input = (key, label, type = "text", required = true) => (
    <div>
      <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--olive)" }}>{label}{required && " *"}</label>
      <input
        type={type}
        required={required}
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`btv-${key}-input`}
        className="w-full px-4 py-3 outline-none"
        style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--ink)" }}
      />
    </div>
  );

  return (
    <div data-testid="before-the-vows-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        <Link to="/counseling" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] mb-10" style={{ color: "var(--ink-2)" }}>
          <ArrowLeft size={14} /> All counseling
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-square w-full max-w-md mx-auto p-10" style={{ background: "#F4ECDE" }}>
              <img src={LOGO} alt="Before the Vows" className="w-full h-full object-contain" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="overline mb-4">Pre-Marriage Counseling</div>
            <h1 className="font-serif-display tracking-[-0.045em] leading-[0.88]" style={{ color: "var(--ink)" }}>
              <span className="block text-5xl sm:text-6xl lg:text-8xl">BEFORE</span>
              <span className="block text-5xl sm:text-6xl lg:text-8xl">
                THE <span className="font-serif-display-italic" style={{ color: "var(--ember)" }}>vows.</span>
              </span>
            </h1>
            <p className="mt-8 text-base md:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
              A pre-marriage counseling program built specifically for <strong style={{ color: "var(--ink)" }}>Oneness Apostolic couples</strong> —
              biblical, doctrinally honest, and designed to give engaged couples the spiritual,
              relational, and practical foundation a strong marriage requires.
            </p>
            <div className="mt-8 flex flex-wrap gap-6 text-sm" style={{ color: "var(--ink-2)" }}>
              <div className="flex items-center gap-2"><Heart size={16} style={{ color: "var(--ember)" }} /> Christ-centered</div>
              <div className="flex items-center gap-2"><Calendar size={16} style={{ color: "var(--ember)" }} /> Flexible scheduling</div>
              <div className="flex items-center gap-2"><Clock size={16} style={{ color: "var(--ember)" }} /> In-person or video</div>
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section style={{ background: "var(--bg-alt)" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="overline mb-3">Two paths · choose what fits</div>
          <h2 className="font-serif-display text-4xl md:text-6xl tracking-[-0.03em] leading-tight" style={{ color: "var(--ink)" }}>
            Pick your program.
          </h2>
          <p className="mt-4 max-w-2xl" style={{ color: "var(--ink-2)" }}>
            Both programs include the full Before the Vows workbook and personal time with Cody. Pay-in-full or split into payments — we'll work it out together.
          </p>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {PROGRAMS.map((p) => (
              <div
                key={p.key}
                data-testid={`btv-program-${p.key}`}
                className="card-surface p-10 relative"
                style={{ borderColor: p.best ? "var(--ember)" : undefined }}
              >
                {p.best && (
                  <div className="absolute -top-3 left-10 px-3 py-1 text-[0.65rem] uppercase tracking-[0.3em]" style={{ background: "var(--ember)", color: "var(--bg-base)" }}>
                    Most thorough
                  </div>
                )}
                <div className="overline">{p.weeks} weeks</div>
                <h3 className="font-serif-display text-3xl mt-3" style={{ color: "var(--ink)" }}>{p.label}</h3>
                <div className="mt-4 flex items-baseline gap-2">
                  <span className="font-serif-display text-5xl" style={{ color: "var(--ink)" }}>${p.price}</span>
                  <span className="text-sm" style={{ color: "var(--muted)" }}>· total program</span>
                </div>
                <p className="mt-5 leading-relaxed" style={{ color: "var(--ink-2)" }}>{p.blurb}</p>
                <ul className="mt-6 space-y-3">
                  {p.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm" style={{ color: "var(--ink-2)" }}>
                      <Check size={16} strokeWidth={2} style={{ color: "var(--olive)", marginTop: 2, flexShrink: 0 }} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => {
                    setForm({ ...form, program: p.key });
                    document.getElementById("btv-form")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid={`btv-select-${p.key}`}
                  className={p.best ? "btn-primary mt-8 w-full justify-center" : "btn-ghost mt-8 w-full justify-center"}
                >
                  Inquire about this program
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INQUIRY FORM */}
      <section id="btv-form" className="max-w-3xl mx-auto px-6 lg:px-12 py-24">
        <div className="overline mb-3">Inquire</div>
        <h2 className="font-serif-display text-4xl md:text-6xl tracking-[-0.03em] leading-tight" style={{ color: "var(--ink)" }}>
          Start the conversation.
        </h2>
        <p className="mt-4 max-w-xl" style={{ color: "var(--ink-2)" }}>
          Fill this out and Cody will personally reach out within a couple of days to set a no-pressure introduction call.
        </p>

        {sent ? (
          <div data-testid="btv-form-success" className="card-surface p-10 mt-12 text-center">
            <Check size={40} strokeWidth={1.5} className="mx-auto" style={{ color: "var(--olive)" }} />
            <h3 className="font-serif-display text-3xl mt-4" style={{ color: "var(--ink)" }}>Inquiry sent.</h3>
            <p className="mt-3" style={{ color: "var(--ink-2)" }}>Thank you. Cody will be in touch soon at the email or phone you provided.</p>
            <button onClick={() => setSent(false)} className="btn-ghost mt-6">Send another</button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="card-surface p-8 lg:p-10 mt-12 space-y-5" data-testid="btv-inquiry-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {input("groom_name", "Groom's name")}
              {input("bride_name", "Bride's name")}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {input("email", "Email", "email")}
              {input("phone", "Phone", "tel")}
            </div>
            {input("wedding_date", "Wedding date (or estimate)", "date", false)}
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--olive)" }}>Which program *</label>
              <select
                required
                value={form.program}
                onChange={(e) => setForm({ ...form, program: e.target.value })}
                data-testid="btv-program-input"
                className="w-full px-4 py-3 outline-none"
                style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--ink)" }}
              >
                <option value="7-week">Foundations — 7 weeks · $525</option>
                <option value="12-week">Comprehensive — 12 weeks · $900</option>
                <option value="not-sure">Not sure — help me decide</option>
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] mb-2" style={{ color: "var(--olive)" }}>Anything you'd like Cody to know</label>
              <textarea
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                data-testid="btv-message-input"
                placeholder="Optional — engagement story, what drew you to this program, preferred schedule, etc."
                className="w-full px-4 py-3 outline-none"
                style={{ background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--ink)" }}
              />
            </div>
            <button type="submit" disabled={busy} data-testid="btv-submit-button" className="btn-primary w-full justify-center disabled:opacity-50">
              {busy ? "Sending…" : "Send inquiry"}
            </button>
            <p className="text-xs" style={{ color: "var(--muted)" }}>
              By submitting you agree to be contacted by Cody by phone or email about Before the Vows. Your info is never shared.
            </p>
          </form>
        )}
      </section>
    </div>
  );
}
